import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { IcnSpinner } from '../../icons/index.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  loading?: boolean;
  /** Leading icon — visually flips to trailing in RTL via flex logical flow. */
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** When `loading`, render content with 0 opacity so layout width is stable. */
  stableWidthOnLoading?: boolean;
  /** Single-line clip with ellipsis when content overflows the max-width. */
  truncate?: boolean;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
}

const SPINNER_SIZE: Record<ButtonSize, number> = { sm: 12, md: 14, lg: 16 };

/** Primary action button.
 *  - Default `type="button"` avoids accidental form submits; pass
 *    `type="submit"` explicitly inside a `<form>`.
 *  - `loading` overlays a spinner and disables interaction; content is
 *    kept in the DOM (so the button keeps its width) but visually hidden.
 *  - `variant: danger` is semantic (destructive actions), not decorative.
 *  - In RTL, the flex container flips so `iconLeft` sits at the visual
 *    leading edge (right side). No extra code required. */
export function Button(props: ButtonProps): ReactNode {
  const {
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    loading = false,
    iconLeft,
    iconRight,
    stableWidthOnLoading = true,
    truncate = false,
    disabled,
    className,
    ref,
    children,
    type = 'button',
    ...rest
  } = props;

  const classes = ['vq-btn', className].filter(Boolean).join(' ');
  const isDisabled = disabled === true || loading;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={classes}
      data-component="Button"
      data-variant={variant}
      data-size={size}
      data-shape={shape}
      data-state={loading ? 'loading' : isDisabled ? 'disabled' : 'idle'}
      data-truncate={truncate ? 'true' : undefined}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <span className="vq-btn__spinner" aria-hidden="true">
          <IcnSpinner size={SPINNER_SIZE[size]} />
        </span>
      ) : null}
      <span
        className="vq-btn__content"
        data-hidden={loading && stableWidthOnLoading ? 'true' : undefined}
      >
        {iconLeft !== undefined ? (
          <span className="vq-btn__icon" data-slot="icon-left" aria-hidden="true">
            {iconLeft}
          </span>
        ) : null}
        <span className="vq-btn__label">{children}</span>
        {iconRight !== undefined ? (
          <span className="vq-btn__icon" data-slot="icon-right" aria-hidden="true">
            {iconRight}
          </span>
        ) : null}
      </span>
    </button>
  );
}
